import { z } from "zod";

import {
  attachmentSourceTypeSchema,
  type AttachmentSourceType,
} from "./attachments";

const trelloIdSchema = z
  .string()
  .regex(/^[a-f0-9]{24}$/, "Expected a 24-character Trello ID.");

const sha256Schema = z
  .string()
  .regex(/^[a-f0-9]{64}$/, "Expected a lowercase SHA-256 digest.");

const archivePathSchema = z
  .string()
  .min(1)
  .max(1_024)
  .superRefine((path, context) => {
    const segments = path.split("/");

    if (
      path.startsWith("/") ||
      path.includes("\\") ||
      segments.some(
        (segment) => segment === "" || segment === "." || segment === "..",
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Archive paths must be normalized relative paths.",
      });
    }
  });

export const snapshotManifestStatusSchema = z.enum([
  "complete",
  "complete_with_warnings",
]);
export type SnapshotManifestStatus = z.infer<
  typeof snapshotManifestStatusSchema
>;

export const attachmentCaptureStatusSchema = z.enum([
  "copied",
  "link_only",
  "skipped",
  "failed",
]);
export type AttachmentCaptureStatus = z.infer<
  typeof attachmentCaptureStatusSchema
>;

export const snapshotWarningObjectTypeSchema = z.enum([
  "board",
  "list",
  "card",
  "label",
  "checklist",
  "check_item",
  "custom_field",
  "custom_field_item",
  "member",
  "comment",
  "action",
  "attachment",
  "snapshot",
]);
export type SnapshotWarningObjectType = z.infer<
  typeof snapshotWarningObjectTypeSchema
>;

const snapshotWarningSchema = z.object({
  code: z.string().regex(/^[A-Z][A-Z0-9_]{2,63}$/),
  message: z.string().min(1).max(500),
  objectId: trelloIdSchema.nullable(),
  objectType: snapshotWarningObjectTypeSchema,
});

const attachmentCaptureSchema = z
  .object({
    archivePath: archivePathSchema.nullable(),
    attachmentId: trelloIdSchema,
    byteSize: z.int().nonnegative().nullable(),
    cardId: trelloIdSchema,
    sha256: sha256Schema.nullable(),
    sourceType: attachmentSourceTypeSchema,
    status: attachmentCaptureStatusSchema,
    warningCode: z
      .string()
      .regex(/^[A-Z][A-Z0-9_]{2,63}$/)
      .nullable(),
  })
  .superRefine((attachment, context) => {
    const copyEligibleSources: readonly AttachmentSourceType[] = [
      "trello_upload",
      "direct_https",
    ];
    const linkOnlySources: readonly AttachmentSourceType[] = [
      ...copyEligibleSources,
      "google_drive",
      "dropbox",
      "onedrive",
    ];
    const copiedFields = [
      attachment.archivePath,
      attachment.byteSize,
      attachment.sha256,
    ];

    if (
      attachment.status === "copied" &&
      copiedFields.some((value) => value === null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Copied attachments require path, size, and checksum.",
      });
    }

    if (
      attachment.status !== "copied" &&
      copiedFields.some((value) => value !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Only copied attachments may contain copied-file metadata.",
      });
    }

    if (
      attachment.status === "copied" &&
      !copyEligibleSources.includes(attachment.sourceType)
    ) {
      context.addIssue({
        code: "custom",
        message: "Only Trello uploads and direct HTTPS files may be copied.",
      });
    }

    if (
      attachment.status === "link_only" &&
      !linkOnlySources.includes(attachment.sourceType)
    ) {
      context.addIssue({
        code: "custom",
        message: "This attachment source cannot be retained as a safe link.",
      });
    }

    const requiresWarning =
      attachment.status === "skipped" || attachment.status === "failed";
    if (requiresWarning !== (attachment.warningCode !== null)) {
      context.addIssue({
        code: "custom",
        message:
          "Skipped and failed attachments require a warning code; successful outcomes must not have one.",
      });
    }
  });

const snapshotObjectCountsSchema = z.object({
  actions: z.int().nonnegative(),
  attachments: z.int().nonnegative(),
  cards: z.int().nonnegative(),
  checkItems: z.int().nonnegative(),
  checklists: z.int().nonnegative(),
  comments: z.int().nonnegative(),
  customFieldItems: z.int().nonnegative(),
  customFields: z.int().nonnegative(),
  labels: z.int().nonnegative(),
  lists: z.int().nonnegative(),
  members: z.int().nonnegative(),
});

export const snapshotManifestV1Schema = z
  .object({
    attachments: z.array(attachmentCaptureSchema),
    capturedAt: z.iso.datetime(),
    counts: snapshotObjectCountsSchema,
    product: z.literal("board-preserve"),
    provider: z.literal("trello"),
    schemaVersion: z.literal(1),
    snapshotId: z.uuid(),
    source: z.object({
      boardId: trelloIdSchema,
      workspaceId: trelloIdSchema.nullable(),
    }),
    status: snapshotManifestStatusSchema,
    warnings: z.array(snapshotWarningSchema),
  })
  .superRefine((manifest, context) => {
    const attachmentIds = new Set<string>();
    const warningKeys = new Set<string>();

    manifest.attachments.forEach((attachment, index) => {
      if (attachmentIds.has(attachment.attachmentId)) {
        context.addIssue({
          code: "custom",
          message: "Attachment IDs must be unique.",
          path: ["attachments", index, "attachmentId"],
        });
      }
      attachmentIds.add(attachment.attachmentId);

      if (
        attachment.warningCode !== null &&
        !manifest.warnings.some(
          (warning) =>
            warning.code === attachment.warningCode &&
            warning.objectType === "attachment" &&
            warning.objectId === attachment.attachmentId,
        )
      ) {
        context.addIssue({
          code: "custom",
          message: "Attachment warning must have a matching manifest warning.",
          path: ["attachments", index, "warningCode"],
        });
      }
    });

    manifest.warnings.forEach((warning, index) => {
      const warningKey = [
        warning.objectType,
        warning.objectId ?? "",
        warning.code,
      ].join(":");

      if (warningKeys.has(warningKey)) {
        context.addIssue({
          code: "custom",
          message: "Manifest warnings must be unique per object and code.",
          path: ["warnings", index],
        });
      }
      warningKeys.add(warningKey);

      if (
        warning.objectType === "attachment" &&
        warning.objectId !== null &&
        !attachmentIds.has(warning.objectId)
      ) {
        context.addIssue({
          code: "custom",
          message:
            "Attachment warning must reference an attachment result in the manifest.",
          path: ["warnings", index, "objectId"],
        });
      }
    });

    if (manifest.counts.attachments !== manifest.attachments.length) {
      context.addIssue({
        code: "custom",
        message:
          "Attachment count must match the attachment result collection.",
        path: ["counts", "attachments"],
      });
    }

    const hasWarnings = manifest.warnings.length > 0;
    if ((manifest.status === "complete_with_warnings") !== hasWarnings) {
      context.addIssue({
        code: "custom",
        message:
          "Complete manifests have no warnings; warning-complete manifests require at least one.",
        path: ["status"],
      });
    }
  });

export type SnapshotManifestV1 = z.infer<typeof snapshotManifestV1Schema>;
